import { FC, useMemo, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { usePageTitle } from "@Hooks/usePageTitle";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Flex,
  Heading,
  Text,
  SimpleGrid,
  HStack,
  Icon,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
  TableContainer,
  useDisclosure,
} from "@chakra-ui/react";
import {
  IconDownload,
  IconArrowLeft,
  IconArrowRight,
  IconAlertTriangle,
  IconRoad,
  IconArrowsRightLeft,
  IconMapPinOff,
  IconCircleCheck,
} from "@tabler/icons-react";
import { Button } from "@Components/Button";
import { RotaHeatmap } from "@Components/RotaHeatmap";
import { ComposicaoPrecoModal } from "@Components/ComposicaoPrecoModal";
import { AcessoNegadoModal } from "@Components/AcessoNegadoModal";
import { formatCurrency, formatCombustivel, corCombustivel } from "@Utils/index";
import { maskCnpj } from "@Utils/Masks/cnpj";
import {
  getEstudoInterno,
  downloadInternoFile,
  AnalyzeResult,
  AnalyzeFileResponse,
  EstudoInternoResponse,
  PostoDetalhe,
} from "@Api/estudo";

// TODO(permissão): gate com "diagnostico.simulacoes.read" quando o código existir.

// Modelo único consumido pela view — normaliza a resposta fresca (analyze-file,
// result completo) e a resposta buscada (/estudo/:token, result sanitizado).
interface StudyView {
  codigo: string;
  empresa: string | null;
  cnpj?: string | null;
  createdAt?: string;
  result: AnalyzeResult;
  economiaRelevante: boolean;
  estudoUrl?: string;
  arquivoUrl?: string;
  pdfUrl?: string;
}

// Estado passado via router na navegação vinda da Nova Consulta (evita refetch e
// preserva o result COMPLETO — /estudo/:token sanitiza quando a economia é baixa).
interface ResultadoLocationState {
  fresh?: AnalyzeFileResponse;
  razaoSocial?: string;
  cnpj?: string;
}

const num2 = (n: number) =>
  n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pct = (frac: number) =>
  (frac * 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }) + "%";
const int = (n: number) =>
  n.toLocaleString("pt-BR", { maximumFractionDigits: 0 });
const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso + "T00:00:00").toLocaleDateString("pt-BR") : null;
const titulo = (s?: string | null) =>
  (s ?? "").toLowerCase().replace(/\b\p{L}/gu, (c) => c.toUpperCase());

function fromFresh(
  f: AnalyzeFileResponse,
  razaoSocial?: string,
  cnpj?: string
): StudyView {
  return {
    codigo: f.codigo,
    empresa: razaoSocial ?? null,
    cnpj: cnpj ?? null,
    // Estudo recém-criado: carimba o momento da geração (o backend não devolve
    // createdAt no fresh) para exibir data/hora no cabeçalho.
    createdAt: new Date().toISOString(),
    result: f.result,
    // Rota interna devolve o result completo (sem corte) → sempre relevante.
    economiaRelevante: f.result.economiaRelevante ?? true,
    estudoUrl: f.estudoUrl,
    arquivoUrl: f.arquivoUrl,
    pdfUrl: f.pdfUrl,
  };
}

function fromEstudoInterno(e: EstudoInternoResponse): StudyView {
  return {
    codigo: e.codigo,
    empresa: e.empresa,
    cnpj: e.cnpj,
    createdAt: e.createdAt,
    result: e.result,
    // Rota interna devolve o result completo (sem corte) → sempre relevante.
    economiaRelevante: e.result.economiaRelevante ?? true,
    estudoUrl: e.estudoUrl,
    arquivoUrl: e.arquivoUrl,
    pdfUrl: e.pdfUrl,
  };
}

const StatCard: FC<{ label: string; value: string; accent?: boolean }> = ({
  label,
  value,
  accent,
}) => (
  <Box
    borderRadius="8px"
    border="1px solid"
    borderColor="neutrals.stroke.soft-200"
    bg="neutrals.surface.surface-0"
    px="16px"
    py="14px"
  >
    <Text
      fontSize="11px"
      fontWeight="600"
      textTransform="uppercase"
      letterSpacing="0.06em"
      color="neutrals.text.soft-400"
      fontFamily="body"
      mb="4px"
    >
      {label}
    </Text>
    <Text
      fontSize="20px"
      fontWeight="700"
      fontFamily="heading"
      color={accent ? "states.success.base" : "neutrals.text.strong-950"}
    >
      {value}
    </Text>
  </Box>
);

// Economia de uma linha (posto) — LÊ o backend (fonte única), não recalcula. `novoPreco`
// = referência efetiva do posto (`precoReferencia`: menor do posto se casado, ou preço do
// indicado se troca); `economia` = `p.economiaTotal` já pronto do backend. Só usada para
// rótulos/decisão de exibição por linha (o total vem de `r.economia*`).
export function economiaLinha(p: PostoDetalhe): {
  temEconomia: boolean;
  novoPreco: number;
  economia: number;
} {
  const economia = p.economiaTotal ?? 0;
  const temEconomia = economia > 0.005;
  const novoPreco = temEconomia && p.precoReferencia != null ? p.precoReferencia : p.precoMedioPago;
  return { temEconomia, novoPreco, economia };
}

// Destaque de economia — herói após o mapa. Regra do time comercial: NUNCA mostrar
// economia negativa. Quando o cliente já compra abaixo da referência (ref >= pago),
// troca o discurso de savings pelo estado honesto "já compra bem".
const EconomiaDestaque: FC<{ r: AnalyzeResult }> = ({ r }) => {
  // FONTE ÚNICA: números vêm do backend (r.*), idênticos a tabela/PDF/XLSX/Chat.
  const anual = r.economiaAnualEstimada ?? 0;
  const mensal = r.economiaMensalEstimada ?? 0;
  const economiaPeriodo = r.economiaPeriodo ?? 0;
  const pago = r.precoMedioPago ?? 0;
  const ref = r.precoMedioReferencia ?? pago;
  const temGanho = economiaPeriodo > 0;
  // Quebra de transparência (R2): credenciado (no cartão) × troca de posto.
  const credAnual = r.economiaRealistaAnual ?? 0;
  const trocaAnual = Math.max(0, anual - credAnual);

  // Com ganho: superfície verde tingida, o número anual lidera (fecha a venda).
  if (temGanho) {
    return (
      <Box
        mt="20px"
        p="22px"
        borderRadius="12px"
        bg="states.success.lighter"
        border="1px solid"
        borderColor="states.success.light"
      >
        <Flex justify="space-between" align="flex-end" wrap="wrap" gap={5}>
          <Box>
            <Text
              fontFamily="heading"
              fontWeight="800"
              fontSize="38px"
              lineHeight="1"
              color="states.success.dark"
            >
              {formatCurrency(anual)}
              <Text as="span" fontSize="17px" fontWeight="600" color="states.success.dark" opacity={0.65}>
                {" "}
                /ano
              </Text>
            </Text>
            <Text fontSize="sm" color="states.success.dark" opacity={0.85} fontFamily="body" mt="8px">
              Economia estimada · ≈ {formatCurrency(mensal)}/mês
              {trocaAnual > 0.5 && (
                <>
                  {" · "}
                  {formatCurrency(credAnual)} no cartão + {formatCurrency(trocaAnual)} trocando de posto
                </>
              )}
            </Text>
          </Box>
          <Box textAlign="right">
            <HStack justify="flex-end" spacing={2}>
              <Text
                fontFamily="heading"
                fontSize="18px"
                color="states.success.dark"
                opacity={0.55}
                textDecoration="line-through"
              >
                {formatCurrency(pago)}/L
              </Text>
              <Icon as={IconArrowRight} boxSize={4} color="states.success.base" />
              <Text fontFamily="heading" fontSize="18px" fontWeight="700" color="states.success.dark">
                {formatCurrency(ref)}/L
              </Text>
            </HStack>
            <Text fontSize="xs" color="states.success.dark" opacity={0.65} fontFamily="body" mt="4px">
              você paga → com o cartão Rede Frota
            </Text>
          </Box>
        </Flex>
      </Box>
    );
  }

  // Sem ganho: estado honesto — o título lidera, sem prometer economia nem mostrar negativo.
  return (
    <Box
      mt="20px"
      p="22px"
      borderRadius="12px"
      bg="neutrals.background.weak-50"
      border="1px solid"
      borderColor="neutrals.stroke.soft-200"
    >
      <Flex justify="space-between" align="flex-end" wrap="wrap" gap={5}>
        <Box maxW="460px">
          <Text
            fontFamily="heading"
            fontWeight="700"
            fontSize="22px"
            color="neutrals.text.strong-950"
          >
            Este cliente já compra bem
          </Text>
          <Text fontSize="sm" color="neutrals.text.sub-600" fontFamily="body" mt="6px">
            Neste período não há economia ao trocar para o cartão — o valor da conversa está na
            cobertura e no consumo.
          </Text>
        </Box>
        <Box textAlign="right">
          <Text fontFamily="heading" fontSize="20px" fontWeight="700" color="neutrals.text.strong-950">
            {formatCurrency(pago)}/L
          </Text>
          <Text fontSize="xs" color="neutrals.text.soft-400" fontFamily="body" mt="4px">
            preço médio · cobertura {pct(r.cobertura)}
          </Text>
        </Box>
      </Flex>
    </Box>
  );
};

const StudyDetail: FC<{ study: StudyView }> = ({ study }) => {
  const r = study.result;
  const porPosto = r.porPosto ?? [];
  const mapaRota = r.mapaRota ?? [];
  // Modal "o que o preço é de fato" (composição das médias por posto).
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [postoSel, setPostoSel] = useState<PostoDetalhe | null>(null);
  const abrirComposicao = (p: PostoDetalhe) => {
    setPostoSel(p);
    onOpen();
  };
  // Abre o modal quando há o que detalhar: composição do pago OU preços de
  // referência do posto (usado/acordo/padrão).
  const temComposicao = (p: PostoDetalhe) =>
    !!(
      p.composicaoPago?.length ||
      p.menorPrecoUsado != null ||
      p.menorPrecoAcordo != null ||
      p.padraoVigente != null
    );
  // economiaLinha (nível de módulo) unifica card × tabela.
  const totalLitros = porPosto.reduce((s, p) => s + p.litros, 0);
  const totalEconomia = porPosto.reduce(
    (s, p) => s + economiaLinha(p).economia,
    0
  );
  // Postos onde a frota abastece SEM cobertura Rede Frota (não casaram).
  const semCobertura = porPosto.filter((p) => !p.casou);
  const dataEstudo = study.createdAt
    ? new Date(study.createdAt).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <Box>
      {/* Aviso de confidencialidade — compartilhamento só via PDF do estudo. */}
      <HStack
        spacing={2}
        align="center"
        mb="16px"
        px="12px"
        py="8px"
        borderRadius="8px"
        border="1px solid"
        borderColor="states.warning.light"
        bg="states.warning.lighter"
      >
        <Icon
          as={IconAlertTriangle}
          boxSize={4}
          color="states.warning.base"
          flexShrink={0}
        />
        <Text fontSize="13px" fontWeight="600" color="states.warning.dark" fontFamily="body">
          Material confidencial de uso interno — só é permitido compartilhar dados do
          estudo em PDF.
        </Text>
      </HStack>

      {/* Cabeçalho */}
      <HStack spacing={3} align="center" wrap="wrap">
        <Heading
          fontSize="24px"
          fontFamily="heading"
          color="neutrals.text.strong-950"
        >
          {study.empresa || "Mapa de rota"}
        </Heading>
        {/* Um selo por produto presente (S-10, S-500). Fallback: combustivel único. */}
        {(r.combustiveis?.length ? r.combustiveis : r.combustivel ? [r.combustivel] : []).map(
          (comb) => {
            const cor = corCombustivel(comb); // cores ANP: S-10 cinza, S-500 vermelho
            return (
              <Text
                key={comb}
                as="span"
                fontSize="12px"
                fontWeight="700"
                px="10px"
                py="3px"
                borderRadius="full"
                fontFamily="body"
                color={cor.fg}
                bg={cor.bg}
                border="1px solid"
                borderColor={cor.border}
              >
                {formatCombustivel(comb)}
              </Text>
            );
          }
        )}
      </HStack>
      <Text
        fontSize="12px"
        fontWeight="600"
        textTransform="uppercase"
        letterSpacing="0.06em"
        color="neutrals.text.soft-400"
        fontFamily="body"
        mt="4px"
      >
        Mapa de rotas e consumo
      </Text>
      <Text fontSize="sm" color="neutrals.text.sub-600" fontFamily="body" mt="4px">
        Referência {study.codigo}
        {study.cnpj ? ` · CNPJ ${maskCnpj(study.cnpj)}` : ""}
        {dataEstudo ? ` · ${dataEstudo}` : ""}
      </Text>

      {r.combustivelAssumido && (
        <HStack
          mt="12px"
          px="12px"
          py="8px"
          bg="states.warning.lighter"
          border="1px solid"
          borderColor="states.warning.light"
          borderRadius="8px"
          spacing={2}
        >
          <Icon as={IconAlertTriangle} boxSize={4} color="states.warning.base" />
          <Text fontSize="xs" color="states.warning.dark" fontFamily="body">
            Não identificamos o tipo de combustível no arquivo — consideramos tudo
            como <strong>diesel S-10</strong>.
          </Text>
        </HStack>
      )}

      {r.aditivadoAproximado && (
        <HStack
          mt="12px"
          px="12px"
          py="8px"
          bg="states.warning.lighter"
          border="1px solid"
          borderColor="states.warning.light"
          borderRadius="8px"
          spacing={2}
        >
          <Icon as={IconAlertTriangle} boxSize={4} color="states.warning.base" />
          <Text fontSize="xs" color="states.warning.dark" fontFamily="body">
            Abastecimentos de <strong>diesel aditivado</strong> foram comparados com o
            preço do diesel comum do mesmo tipo (S-10/S-500).
          </Text>
        </HStack>
      )}

      {/* Mapa de Rota (heatmap dos abastecimentos) — herói da página */}
      <Heading
        fontSize="18px"
        fontFamily="heading"
        color="neutrals.text.strong-950"
        mt="24px"
        mb="12px"
      >
        Mapa de rota
      </Heading>
      <RotaHeatmap points={mapaRota} />

      {/* Destaque de economia (ou estado honesto "já compra bem") */}
      <EconomiaDestaque r={r} />

      {/* Métricas de consumo */}
      <SimpleGrid columns={{ base: 2, md: 3 }} spacing="12px" mt="16px">
        <StatCard label="Litros totais" value={num2(r.litrosTotais)} />
        <StatCard label="Cobertura" value={pct(r.cobertura)} />
        <StatCard
          label="Preço médio pago"
          value={`${formatCurrency(r.precoMedioPago ?? 0)}/L`}
        />
        <StatCard label="Nº de postos" value={int(porPosto.length)} />
        <StatCard
          label="Período"
          value={
            r.dataMin && r.dataMax
              ? `${fmtDate(r.dataMin)} – ${fmtDate(r.dataMax)}`
              : `${num2(r.meses)} meses`
          }
        />
      </SimpleGrid>

      {/* Consumo FORA da plataforma (abastecimento manual) — separado da análise
          on-platform. Oportunidade de trazer o volume para o cartão Rede Frota. */}
      {r.foraPlataforma && r.foraPlataforma.litros > 0 && (
        <Box
          mt="16px"
          px="16px"
          py="14px"
          borderRadius="10px"
          border="1px solid"
          borderColor="neutrals.stroke.soft-200"
          bg="neutrals.background.weak-50"
        >
          <HStack spacing={2} align="center" mb="8px">
            <Icon as={IconArrowsRightLeft} boxSize={4} color="neutrals.text.sub-600" />
            <Text fontSize="13px" fontWeight="700" color="neutrals.text.strong-950" fontFamily="body">
              Consumo fora da plataforma
            </Text>
          </HStack>
          <Text fontSize="12px" color="neutrals.text.sub-600" fontFamily="body" mb="10px">
            Abastecimentos <strong>manuais</strong> (fora do cartão Rede Frota) — não entram na
            análise por posto acima. Oportunidade de trazer esse volume para a rede.
          </Text>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing="12px">
            <StatCard label="Litros" value={`${num2(r.foraPlataforma.litros)} L`} />
            <StatCard
              label="Valor pago"
              value={formatCurrency(r.foraPlataforma.valorPago)}
            />
            <StatCard
              label="Preço médio"
              value={`${formatCurrency(r.foraPlataforma.precoMedio)}/L`}
            />
            <StatCard label="Nº de postos" value={int(r.foraPlataforma.nPostos)} />
          </SimpleGrid>
        </Box>
      )}

      {/* Aviso de cobertura: postos onde a frota abastece fora da rede RF */}
      {semCobertura.length > 0 && (
        <HStack
          mt="16px"
          px="14px"
          py="12px"
          bg="states.warning.lighter"
          border="1px solid"
          borderColor="states.warning.light"
          borderRadius="8px"
          spacing={3}
          align="flex-start"
        >
          <Icon as={IconAlertTriangle} boxSize={4} color="states.warning.base" mt="2px" />
          <Text fontSize="sm" color="states.warning.dark" fontFamily="body">
            <strong>{semCobertura.length}</strong> de {porPosto.length} postos não credenciados
            Rede Frota. Sugerimos o posto credenciado mais próximo de cada um, priorizando a{" "}
            <strong>mesma rodovia</strong>.
          </Text>
        </HStack>
      )}

      {/* Detalhe por posto */}
      <Heading
        fontSize="18px"
        fontFamily="heading"
        color="neutrals.text.strong-950"
        mt="28px"
        mb="4px"
      >
        Detalhe por posto
      </Heading>
      <Text fontSize="12px" color="neutrals.text.sub-600" fontFamily="body" mb="12px">
        Todos os preços são <strong>médias ponderadas por litros</strong> no período.
      </Text>
      <TableContainer
        borderRadius="8px"
        border="1px solid"
        borderColor="neutrals.stroke.soft-200"
        bg="neutrals.surface.surface-0"
      >
        <Table variant="simple" size="sm">
          <Thead bg="neutrals.background.weak-50">
            <Tr>
              <Th textTransform="none" isNumeric>
                #
              </Th>
              <Th textTransform="none" minW="200px">Posto</Th>
              <Th textTransform="none" minW="150px">Cidade/UF</Th>
              <Th textTransform="none" isNumeric>
                Litros (L)
              </Th>
              <Th textTransform="none" isNumeric>
                Valor pago (R$/L)
              </Th>
              <Th textTransform="none" isNumeric>
                Menor preço médio (R$/L)
              </Th>
              <Th textTransform="none" isNumeric>
                Preço padrão (R$/L)
              </Th>
              <Th textTransform="none" isNumeric>
                Econ. total (R$)
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {porPosto.map((p, i) => {
              const { temEconomia, economia, novoPreco } = economiaLinha(p);
              // Não credenciado cuja economia vem do posto INDICADO (troca).
              const refIndicado = !p.casou && temEconomia;
              const clicavel = temComposicao(p);
              return (
                <Tr
                  key={i}
                  {...(clicavel && {
                    cursor: "pointer",
                    onClick: () => abrirComposicao(p),
                    _hover: { bg: "neutrals.background.weak-50" },
                  })}
                >
                  <Td isNumeric verticalAlign="top">
                    {i + 1}
                  </Td>
                  {/* Posto: nome + status/troca embutidos (sem coluna "Cobertura RF"). */}
                  <Td verticalAlign="top" minW="200px" maxW="340px" whiteSpace="normal">
                    <Text
                      fontSize="13px"
                      fontWeight="600"
                      color="neutrals.text.strong-950"
                    >
                      {titulo(p.nome) || "—"}
                    </Text>
                    {p.casou ? (
                      <HStack spacing="4px" mt="2px" align="center">
                        <Icon
                          as={IconCircleCheck}
                          boxSize="13px"
                          color="states.success.base"
                        />
                        <Text fontSize="11px" fontWeight="600" color="states.success.dark">
                          Credenciado Rede Frota
                        </Text>
                      </HStack>
                    ) : p.sugestao ? (
                      <HStack spacing="4px" mt="2px" align="flex-start">
                        <Icon
                          as={p.sugestao.mesmaBr ? IconRoad : IconArrowsRightLeft}
                          boxSize="13px"
                          mt="2px"
                          flexShrink={0}
                          color={
                            p.sugestao.mesmaBr
                              ? "states.success.base"
                              : "neutrals.text.sub-600"
                          }
                        />
                        <Box>
                          <Text fontSize="11px" color="neutrals.text.sub-600">
                            Trocar por{" "}
                            {titulo(p.sugestao.razaoSocial) || "Posto Rede Frota"} ·{" "}
                            {int(p.sugestao.distanciaKm)} km
                          </Text>
                          <Text
                            fontSize="11px"
                            fontWeight="600"
                            color={
                              p.sugestao.mesmaBr
                                ? "states.success.dark"
                                : "neutrals.text.sub-600"
                            }
                          >
                            {p.sugestao.mesmaBr
                              ? `Mesma rodovia${p.sugestao.brComum ? ` · BR-${p.sugestao.brComum}` : ""}`
                              : "Outra rodovia"}
                          </Text>
                        </Box>
                      </HStack>
                    ) : (
                      <HStack spacing="4px" mt="2px" align="center">
                        <Icon
                          as={IconMapPinOff}
                          boxSize="13px"
                          color="neutrals.text.soft-400"
                        />
                        <Text fontSize="11px" color="neutrals.text.soft-400">
                          Sem posto a menos de 50km
                        </Text>
                      </HStack>
                    )}
                  </Td>
                  <Td verticalAlign="top">
                    {p.cidade || "—"}
                    {p.estado ? `/${p.estado}` : ""}
                  </Td>
                  <Td isNumeric verticalAlign="top">
                    {num2(p.litros)}
                  </Td>
                  <Td isNumeric verticalAlign="top">
                    <Text
                      as="span"
                      borderBottom={clicavel ? "1px dotted" : undefined}
                      borderColor="neutrals.stroke.sub-300"
                    >
                      {num2(p.precoMedioPago)}
                    </Text>
                  </Td>
                  {/* Menor preço médio (média dos menores diários) — detalhe no modal.
                      Posto não credenciado: mostra a referência do posto INDICADO (é ela
                      que gera a economia), rotulada, para pago − ref = economia fechar. */}
                  <Td isNumeric verticalAlign="top">
                    {(p.menorPrecoMedioDiario ?? p.menorPrecoUsado) != null ? (
                      <Text
                        as="span"
                        borderBottom={clicavel ? "1px dotted" : undefined}
                        borderColor="neutrals.stroke.sub-300"
                      >
                        {num2((p.menorPrecoMedioDiario ?? p.menorPrecoUsado) as number)}
                      </Text>
                    ) : refIndicado ? (
                      <Text as="span">{num2(novoPreco)}</Text>
                    ) : (
                      "—"
                    )}
                  </Td>
                  {/* Preço padrão (balcão vigente): do próprio posto se CREDENCIADO; se for
                      troca, o padrão do posto INDICADO (contexto do novo posto). */}
                  <Td isNumeric verticalAlign="top">
                    {p.casou && p.padraoVigente != null
                      ? num2(p.padraoVigente)
                      : refIndicado && p.sugestao?.padraoVigente != null
                        ? num2(p.sugestao.padraoVigente)
                        : "—"}
                  </Td>
                  {/* Economia total (vs. menor preço já praticado) — compat p/ postos não
                      casados usa a economiaLinha (posto indicado). */}
                  <Td isNumeric verticalAlign="top" color="states.success.dark">
                    {temEconomia ? num2(economia) : "—"}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
          <Tfoot>
            <Tr
              bg="neutrals.background.weak-50"
              sx={{ "& > th": { py: "14px", borderTop: "2px solid", borderColor: "neutrals.stroke.soft-200" } }}
            >
              <Th />
              <Th textTransform="none" fontSize="12px" color="neutrals.text.strong-950">
                Total no período
              </Th>
              <Th />
              <Th isNumeric fontSize="12px" color="neutrals.text.strong-950">
                {num2(totalLitros)}
              </Th>
              <Th />
              <Th />
              <Th />
              <Th isNumeric fontSize="12px" color="states.success.dark">
                {num2(totalEconomia)}
              </Th>
            </Tr>
          </Tfoot>
        </Table>
      </TableContainer>

      <ComposicaoPrecoModal isOpen={isOpen} onClose={onClose} posto={postoSel} />
    </Box>
  );
};

export const ResultadoPage: FC = () => {
  usePageTitle("Estudo de Rota");
  const { token } = useParams<{ token: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state as ResultadoLocationState | null) ?? null;

  // Estudo recém-criado chega via router state (result completo). Sem state
  // (ex.: histórico, refresh), busca por /estudo/:token — que vem sanitizado.
  const freshView = useMemo<StudyView | null>(() => {
    if (state?.fresh) {
      return fromFresh(state.fresh, state.razaoSocial, state.cnpj);
    }
    return null;
  }, [state]);

  const {
    data: fetched,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["estudo", token],
    queryFn: () => getEstudoInterno(token as string),
    enabled: !freshView && !!token,
  });

  // 403 = estudo de um TIPO que o papel do usuário não cobre → modal (não "não
  // encontrada"). Ao fechar, volta ao histórico (que já lista só o que ele pode ver).
  const acessoNegado =
    !freshView && (error as { response?: { status?: number } })?.response?.status === 403;

  const study: StudyView | null =
    freshView ?? (fetched ? fromEstudoInterno(fetched) : null);

  return (
    <Box>
      <Flex justify="space-between" align="center" mb="20px" wrap="wrap" gap={3}>
        <Button
          colorSchema="neutral"
          variant="ghost"
          size="sm"
          leftIcon={<Icon as={IconArrowLeft} boxSize={4} />}
          onClick={() => navigate("/historico")}
        >
          Voltar ao histórico
        </Button>
        {study?.arquivoUrl && (
          <HStack spacing={2}>
            {study.pdfUrl && (
              <Button
                colorSchema="brand"
                variant="solid"
                size="sm"
                onClick={() => downloadInternoFile(study.pdfUrl as string)}
                leftIcon={<Icon as={IconDownload} boxSize={4} />}
              >
                Baixar relatório (PDF)
              </Button>
            )}
            {study.estudoUrl && (
              <Button
                colorSchema="brand"
                variant="outline"
                size="sm"
                onClick={() => downloadInternoFile(study.estudoUrl as string)}
                leftIcon={<Icon as={IconDownload} boxSize={4} />}
              >
                Exportar (Excel)
              </Button>
            )}
            <Button
              colorSchema="neutral"
              variant="outline"
              size="sm"
              onClick={() => downloadInternoFile(study.arquivoUrl as string)}
              leftIcon={<Icon as={IconDownload} boxSize={4} />}
            >
              Arquivo original
            </Button>
          </HStack>
        )}
      </Flex>

      {!freshView && isLoading && (
        <Flex align="center" justify="center" py={20} gap={3}>
          <Spinner color="primary.light.base" />
          <Text color="neutrals.text.sub-600" fontFamily="body">
            Carregando…
          </Text>
        </Flex>
      )}

      {!freshView && isError && !acessoNegado && (
        <Box textAlign="center" py={20}>
          <Heading fontSize="20px" fontFamily="heading" color="neutrals.text.strong-950">
            Análise não encontrada
          </Heading>
          <Text mt="8px" color="neutrals.text.sub-600" fontFamily="body">
            O link pode ter expirado ou estar incorreto.
          </Text>
        </Box>
      )}

      {study && <StudyDetail study={study} />}

      <AcessoNegadoModal
        isOpen={acessoNegado}
        onClose={() => navigate("/historico")}
        titulo="Sem acesso a este estudo"
        mensagem="Este estudo é de um tipo que sua conta não pode visualizar. Volte ao histórico para ver os estudos disponíveis para você."
        botaoLabel="Voltar ao histórico"
      />
    </Box>
  );
};
